// Forward-migration for canvases saved before a node's data shape changed. Both places a canvas is
// read back verbatim — the server's lossless `builderJson` blob and the local draft store — hand
// their nodes through here first, so an old save opens as the shape the current builder renders
// instead of crashing on a field that moved.
//
// The rule these follow is the one deserialize.ts follows: an upgrade may only ADD what the old
// shape already implied. It must never substitute a default for config that was really stored,
// because the canvas is what the next save writes back to a running workflow.
import type { EditObjectNodeData, WorkflowNode } from 'app/types/workflows'

/** The pre-multi-field Edit node: one `field`/`operation`/`value` triple on the node itself. */
type LegacyEditObjectNodeData = {
  field?: string
  operation?: string
  value?: string
}

/** Nodes as they came out of storage — `data` is whatever shape was current when it was saved. */
type StoredNode = Omit<WorkflowNode, 'data'> & { data?: Record<string, unknown> }

export function upgradeStoredNodes(nodes: WorkflowNode[]): WorkflowNode[] {
  return nodes.map((node) => {
    if (node.type !== `editObject`) return node
    const stored = node as StoredNode
    const data = (stored.data ?? {}) as LegacyEditObjectNodeData & Partial<EditObjectNodeData>
    if (Array.isArray(data.fields)) return node
    // An untouched palette default (no field chosen) upgrades to no rows rather than one blank one,
    // so the node reads as "nothing configured" in exactly the way a new one does.
    const rows = data.field
      ? [
          {
            field: data.field,
            operation: data.operation === `clear` ? (`clear` as const) : (`set` as const),
            value: data.value ?? ``,
          },
        ]
      : []
    const { field: _field, operation: _operation, value: _value, ...rest } = data
    return { ...stored, data: { ...rest, fields: rows } } as WorkflowNode
  })
}
